import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resourceColor } from './layout';

export interface FilterResource {
  id: string;
  name: string;
  colorIndex: number;
}

interface Props {
  resources: FilterResource[];
  // An empty selection means all resources.
  selected: string[];
  onChange: (selected: string[]) => void;
}

export const ResourceFilter: React.FC<Props> = ({ resources, selected, onChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const isSelected = (id: string) => selected.length === 0 || selected.includes(id);

  const toggle = (id: string) => {
    const base = selected.length === 0 ? resources.map(resource => resource.id) : selected;
    const next = base.includes(id) ? base.filter(item => item !== id) : [...base, id];
    // At least one stays selected; selecting everything is the same as no filter.
    if (next.length === 0) return;
    onChange(next.length === resources.length ? [] : next);
  };

  const summary =
    selected.length === 0
      ? t('schedulingAdmin.calendar.allResources')
      : t('schedulingAdmin.calendar.resourcesSelected', {
          count: selected.length,
          total: resources.length,
        });

  return (
    <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      <div className="flex items-center gap-3 px-3 py-1.5">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="flex items-center gap-1 text-sm font-medium text-gray-900 dark:text-gray-100"
        >
          <span className="material-icons text-base text-gray-500">
            {open ? 'expand_less' : 'expand_more'}
          </span>
          {t('schedulingAdmin.calendar.resourcesFilter')}
        </button>
        <span className="text-sm text-gray-500 dark:text-gray-400">{summary}</span>
        {!open && (
          <div className="flex flex-wrap items-center gap-1">
            {resources
              .filter(resource => isSelected(resource.id))
              .map(resource => (
                <span
                  key={resource.id}
                  title={resource.name}
                  className={`inline-block h-2.5 w-2.5 rounded-full ${resourceColor(resource.colorIndex).dot}`}
                />
              ))}
          </div>
        )}
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="ml-auto text-sm text-blue-600 hover:underline"
          >
            {t('schedulingAdmin.calendar.resourcesReset')}
          </button>
        )}
      </div>
      {open && (
        <div className="flex flex-wrap gap-2 border-t border-gray-200 px-3 py-2 dark:border-gray-700">
          {resources.map(resource => {
            const active = isSelected(resource.id);
            const color = resourceColor(resource.colorIndex);
            return (
              <button
                key={resource.id}
                type="button"
                aria-pressed={active}
                onClick={() => toggle(resource.id)}
                className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm ${
                  active
                    ? `border-l-4 font-medium ${color.block}`
                    : 'border-dashed border-gray-300 bg-white text-gray-400 line-through hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-500 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons text-sm">
                  {active ? 'check_circle' : 'radio_button_unchecked'}
                </span>
                {resource.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
